import { ApiField, ApiFieldTypes, ApiSection } from "@/types/custom-field.types.ts";
import {
  ApiApplyFieldConfigSave,
  ApiApplyFormSectionSave,
  ApiJobApplyFormSection,
} from "@/types/recruit/job.types.ts";

export interface JobApplyFormLayoutField {
  field: ApiField;
  is_mandatory: boolean;
  is_hidden: boolean;
}

export interface JobApplyFormLayoutSection {
  section: ApiSection;
  fields: JobApplyFormLayoutField[];
}

export const toJobApplyFormSave = (
  sections: ApiJobApplyFormSection[] | null | undefined
): ApiApplyFormSectionSave[] | null =>
  sections?.map((section) => ({
    section_id: section.section.id,
    fields: section.fields.map((item) => ({
      field_id: item.field.id,
      is_mandatory: item.is_mandatory && !item.field.is_mandatory,
      is_hidden: item.is_hidden,
    })),
  })) ?? null;

const byConfigThenOrg =
  <T>(getOrder: (item: T) => number | undefined) =>
  (a: T, b: T) => {
    const orderA = getOrder(a);
    const orderB = getOrder(b);
    if (orderA === undefined || orderB === undefined) {
      return Number(orderA === undefined) - Number(orderB === undefined);
    }
    return orderA - orderB;
  };

export const buildJobApplyFormLayout = (
  sections: ApiSection[],
  fields: ApiField[],
  config: ApiApplyFormSectionSave[] | null
): JobApplyFormLayoutSection[] => {
  const sectionOrder = new Map((config ?? []).map((item, index) => [item.section_id, index]));
  const fieldConfigs = new Map<
    number,
    ApiApplyFieldConfigSave & { section_id: number; sort: number }
  >(
    (config ?? []).flatMap((section) =>
      section.fields.map(
        (field, sort) =>
          [field.field_id, { ...field, section_id: section.section_id, sort }] as const
      )
    )
  );

  const visibleSections = sections
    .filter((section) => !section.is_hidden && !section.is_archived)
    .sort(byConfigThenOrg((section) => sectionOrder.get(section.id)));
  const visibleSectionIds = new Set(visibleSections.map((section) => section.id));
  const getSectionId = (field: ApiField) => {
    const configSectionId = fieldConfigs.get(field.id)?.section_id;
    return configSectionId !== undefined && visibleSectionIds.has(configSectionId)
      ? configSectionId
      : field.section_id;
  };
  const visibleFields = fields.filter(
    (field) =>
      !field.is_hidden &&
      !field.is_archived &&
      field.type !== ApiFieldTypes.applicant_additional_contacts
  );

  return visibleSections.map((section) => ({
    section,
    fields: visibleFields
      .filter((field) => getSectionId(field) === section.id)
      .sort(byConfigThenOrg((field) => fieldConfigs.get(field.id)?.sort))
      .map((field) => {
        const fieldConfig = fieldConfigs.get(field.id);
        const isMandatory = !!fieldConfig?.is_mandatory;
        return {
          field,
          is_mandatory: isMandatory,
          is_hidden: !!fieldConfig?.is_hidden && !isMandatory && !field.is_mandatory,
        };
      }),
  }));
};

export const toJobApplyFormSaveFromLayout = (
  layout: JobApplyFormLayoutSection[]
): ApiApplyFormSectionSave[] =>
  layout.map((section) => ({
    section_id: section.section.id,
    fields: section.fields.map((item) => ({
      field_id: item.field.id,
      is_mandatory: item.is_mandatory,
      is_hidden: item.is_hidden,
    })),
  }));

export const moveJobApplyFormSection = (
  layout: JobApplyFormLayoutSection[],
  fromIndex: number,
  toIndex: number
): JobApplyFormLayoutSection[] => {
  const next = [...layout];
  const [moved] = next.splice(fromIndex, 1);
  if (!moved) return layout;
  next.splice(toIndex, 0, moved);
  return next;
};

export const moveJobApplyFormField = (
  layout: JobApplyFormLayoutSection[],
  from: { sectionId: number; index: number },
  to: { sectionId: number; index: number }
): JobApplyFormLayoutSection[] => {
  const moved = layout.find((section) => section.section.id === from.sectionId)?.fields[from.index];
  if (!moved) return layout;

  return layout.map((section) => {
    let fields = section.fields;
    if (section.section.id === from.sectionId) {
      fields = fields.filter((_, index) => index !== from.index);
    }
    if (section.section.id === to.sectionId) {
      fields = [...fields.slice(0, to.index), moved, ...fields.slice(to.index)];
    }
    return fields === section.fields ? section : { ...section, fields };
  });
};
